import styled, { createGlobalStyle } from 'styled-components';
import { ThemeType } from './colors';

export const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${props => props.theme.background};
    color: ${props => props.theme.textPrimary};
    line-height: 1.6;
  }

  html {
    font-size: 16px;
  }

  #root {
    min-height: 100vh;
  }

  h1, h2, h3, h4, h5, h6 {
    color: ${props => props.theme.textPrimary};
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  h1 {
    font-size: 2rem;
  }

  h2 {
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.25rem;
  }

  p {
    color: ${props => props.theme.textSecondary};
    margin-bottom: 1rem;
  }

  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    outline: none;
    transition: all 0.3s ease;
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  input, textarea, select {
    font-family: inherit;
    outline: none;
    transition: all 0.3s ease;
    /* Prevent iOS zoom on focus */
    font-size: 16px;
    /* Enable smooth scrolling on iOS */
    -webkit-overflow-scrolling: touch;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    html {
      font-size: 14px;
    }

    h1 {
      font-size: 1.75rem;
    }

    h2 {
      font-size: 1.25rem;
    }
  }

  /* iOS Safari input fixes */
  @supports (-webkit-touch-callout: none) {
    input:focus,
    textarea:focus,
    select:focus {
      font-size: 16px !important;
    }
  }
`;


// Common styled components
export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
`;

export const MobileContainer = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 1rem;
  min-height: 100vh;
`;

export const Card = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: ${props => props.theme.shadow};
  border: 1px solid ${props => props.theme.border};
`;

export const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'outline' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  
  ${props => {
    switch (props.variant) {
      case 'secondary':
        return `
          background: ${props.theme.buttonSecondary};
          color: ${props.theme.white};
          
          &:hover:not(:disabled) {
            background: ${props.theme.buttonSecondaryHover};
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${props.theme.primary};
          border: 2px solid ${props.theme.primary};
          
          &:hover:not(:disabled) {
            background: ${props.theme.primary};
            color: ${props.theme.white};
          }
        `;
      default:
        return `
          background: ${props.theme.buttonPrimary};
          color: ${props.theme.white};
          
          &:hover:not(:disabled) {
            background: ${props.theme.buttonPrimaryHover};
          }
        `;
    }
  }}
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.inputBorder};
  border-radius: 8px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    border-color: ${props => props.theme.inputFocus};
    outline: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.textLight};
  }
`;

export const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

export const SuccessMessage = styled.div`
  color: ${props => props.theme.success};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

export const Logo = styled.img`
  height: 60px;
  width: auto;
  object-fit: contain;
  background: #F40000;
  padding: 10px;
  border-radius: 8px;
`;